import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import { AudioFile, NaatKhawan as NaatKhawanType } from '../types';
import { Music, User, Play, ChevronRight } from 'lucide-react';
import { BASE_DOMAIN } from '../config';
import NaatCard from '../components/NaatCard';
import SEO from '../components/SEO';
import NotFound from '../components/NotFound';
import './NaatKhawanDetailPage.css';

interface LocalAudioFile {
  id: number;
  title: string;
  duration: string;
  file_path: string;
  category: string;
  play_count?: number;
  playCount?: number;  // Also check for camelCase
}

const NaatKhawanDetailPage: React.FC = () => {
  const { id, page: pageParam, letter: letterParam } = useParams<{ id: string; page?: string; letter?: string }>();
  const navigate = useNavigate();
  const { playAudio, addToQueue, togglePlay, state } = useAudioPlayer();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();
  const { currentAudio, isPlaying } = state;
  const [naatKhawan, setNaatKhawan] = useState<NaatKhawanType | null>(null);
  const [audioFiles, setAudioFiles] = useState<LocalAudioFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [initial, setInitial] = useState<string>(letterParam?.toUpperCase() || '');
  const [error, setError] = useState<string | null>(null);
  const [is404Error, setIs404Error] = useState(false);
  const [availableLetters, setAvailableLetters] = useState<Set<string>>(new Set());
  const [pagination, setPagination] = useState({
    page: pageParam ? parseInt(pageParam, 10) : 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false
  });

  const fetchNaatKhawanDetails = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);

      // Fetch NaatKhawan details
      const naatKhawanResponse = await fetch(`${BASE_DOMAIN}/api/naatkhawans/${id}`);
      if (!naatKhawanResponse.ok) {
        if (naatKhawanResponse.status === 404) {
          setIs404Error(true);
        }
        throw new Error('Failed to fetch NaatKhawan details');
      }
      const naatKhawanData = await naatKhawanResponse.json();
      setNaatKhawan(naatKhawanData);
      setIs404Error(false);

      // Set document title for SEO
      if (naatKhawanData && naatKhawanData.name) {
        document.title = `${naatKhawanData.name} : NaatCity`;
      }

      // Fetch audio files for this NaatKhawan with pagination
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '15'); // Use reasonable limit for pagination
      if (initial) {
        params.append('initial', initial);
      }

      const audioResponse = await fetch(`${BASE_DOMAIN}/api/naatkhawans/${id}/audios?${params.toString()}`);
      if (!audioResponse.ok) {
        throw new Error('Failed to fetch audio files');
      }
      const responseData = await audioResponse.json();

      // Handle both new and old API response formats
      let audioData, paginationData, availableLettersData;

      if (responseData.data) {
        // New format with pagination
        audioData = responseData.data;
        paginationData = responseData.pagination;
        availableLettersData = responseData.availableLetters || [];
      } else {
        // Old format - backward compatibility
        audioData = responseData;
        paginationData = {
          page: 1,
          totalPages: 1,
          total: audioData.length,
          hasNext: false,
          hasPrev: false
        };
        availableLettersData = [];
      }

      // Map playCount from API response (now included in the main query)
      const audioWithDetails = audioData.map((audio: LocalAudioFile) => ({
        ...audio,
        playCount: audio.play_count || audio.playCount || 0
      }));

      setAudioFiles(audioWithDetails);
      setPagination(paginationData);

      // Set available letters from API response (only for first page without filter)
      if (!initial && page === 1 && availableLettersData.length > 0) {
        setAvailableLetters(new Set(availableLettersData));
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [id, initial]);

  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    // Scroll the actual scrollable containers based on index.css setup
    document.body.scrollTop = 0;

    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }

    // Also try with a small delay to ensure DOM is ready
    setTimeout(() => {
      document.body.scrollTop = 0;
      if (rootElement) {
        rootElement.scrollTop = 0;
      }
    }, 0);
  }, [id]);

  useEffect(() => {
    const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
    fetchNaatKhawanDetails(currentPage);
    setPagination(prev => ({ ...prev, page: currentPage }));
  }, [fetchNaatKhawanDetails, pageParam]);

  // Sync letter filter from URL
  useEffect(() => {
    if (letterParam) {
      setInitial(letterParam.toUpperCase());
    } else {
      setInitial('');
    }
  }, [letterParam]);

  // Reset title when component unmounts
  useEffect(() => {
    return () => {
      document.title = 'NaatCity - Islamic Audio Streaming Platform';
    };
  }, []);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const handleInitialClick = (letter: string) => {
    // Only allow click if the letter has naats
    if (availableLetters.has(letter)) {
      const isTogglingOff = initial === letter;
      const newLetter = isTogglingOff ? '' : letter;
      setInitial(newLetter);
      setPagination(prev => ({ ...prev, page: 1 }));
      
      // Navigate to appropriate URL
      if (newLetter) {
        navigate(`/naatkhawan/${id}/letter/${newLetter.toLowerCase()}`, { replace: true });
      } else {
        navigate(`/naatkhawan/${id}`, { replace: true });
      }
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      fetchNaatKhawanDetails(newPage);
      
      // Navigate to appropriate URL
      let url = `/naatkhawan/${id}`;
      if (initial) {
        url += `/letter/${initial.toLowerCase()}`;
      }
      if (newPage > 1) {
        url += `/page/${newPage}`;
      }
      navigate(url, { replace: true });
      
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Convert duration string "mm:ss" to seconds
  const parseDuration = (durationStr: string): number => {
    if (!durationStr) return 0;
    const parts = durationStr.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    }
    return 0;
  };

  const handlePlayAudio = (audioFile: AudioFile) => {
    // If this audio is currently playing, toggle play/pause
    if (currentAudio?.id === audioFile.id) {
      togglePlay();
      return;
    }
    playAudio(audioFile);
  };

  const handleToggleFavorite = (audioFile: AudioFile) => {
    const wasAlreadyFavorite = isFavorite(audioFile.id);
    toggleFavorite(audioFile);

    // Check if global player is visible (not minimized)
    const isPlayerMinimized = !document.querySelector('.global-audio-player:not(.minimized)');
    const toastPosition = isPlayerMinimized ? 'bottom-right' : 'top-right';

    if (wasAlreadyFavorite) {
      showToast(`Removed "${audioFile.title}" from favorites`, 'info', 3000, toastPosition);
    } else {
      showToast(`Added "${audioFile.title}" to favorites`, 'success', 3000, toastPosition);
    }
  };

  const handleAddToQueue = (audioFile: AudioFile) => {
    const added = addToQueue(audioFile);
    const isPlayerMinimized = !document.querySelector('.global-audio-player:not(.minimized)');
    if (added) {
      showToast(`Added "${audioFile.title}" to queue`, 'success', 3000, isPlayerMinimized ? 'bottom-right' : 'top-right');
    } else {
      showToast(`"${audioFile.title}" is already in queue`, 'info', 3000, isPlayerMinimized ? 'bottom-right' : 'top-right');
    }
  };

  const handleDownloadComplete = (audio: AudioFile, success: boolean) => {
    if (success) {
      showToast(`Downloaded "${audio.title}" successfully! 🎵`, 'success', 4000, 'bottom-right');
    } else {
      showToast(`Failed to download "${audio.title}". Please try again.`, 'error', 4000, 'bottom-right');
    }
  };

  // Convert LocalAudioFile to AudioFile for NaatCard
  const convertToAudioFile = (audio: LocalAudioFile): AudioFile => {
    const rawPath = audio.file_path;
    const isAbsolute = /^https?:\/\//i.test(rawPath);
    const normalizedPath = isAbsolute ? rawPath : (rawPath.startsWith('/') ? rawPath : `/${rawPath}`);

    return {
      id: audio.id,
      title: audio.title,
      naatKhawanId: parseInt(id || '0'),
      naatKhawan: naatKhawan || undefined,
      filePath: normalizedPath,
      duration: parseDuration(audio.duration),
      playCount: audio.playCount || audio.play_count || 0,
      language: 'urdu' as const,
      category: audio.category,
      uploadDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  };

  if (loading) {
    return (
      <div className="naatkhawan-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Naat Khawan details and Naat List ...</p>
        </div>
      </div>
    );
  }

  if (error || !naatKhawan) {
    // Show the beautiful NotFound component for 404 errors
    if (is404Error) {
      return <NotFound type="naatkhawan" />;
    }

    // Show regular error for other types of errors
    return (
      <div className="naatkhawan-detail-page">
        <div className="error-container">
          <User size={64} />
          <h2>Unable to load NaatKhawan</h2>
          <p>{error || 'NaatKhawan not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="naatkhawan-detail-page">
      {naatKhawan && (
        <SEO
          title={`${naatKhawan.name} - NaatCity`}
          description={`Explore the complete collection of ${audioFiles.length} Islamic naats by ${naatKhawan.name}${naatKhawan.urduName ? ` (${naatKhawan.urduName})` : ''} on NaatCity. Listen to beautiful spiritual recitations in high-quality audio streaming.`}
          keywords={`${naatKhawan.name}, ${naatKhawan.urduName || ''}, naat khawan profile, islamic reciter, spiritual naats, urdu naats collection, ${audioFiles.length} naats`}
          url={`https://naatcity.com/naatkhawan/${naatKhawan.id}`}
          type="profile"
          naatKhawan={{
            name: naatKhawan.name,
            naatCount: audioFiles.length
          }}
        />
      )}

      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-container">
        <div className="breadcrumb">
          <Link to="/" className="breadcrumb-link">Home</Link>
          <ChevronRight size={16} className="breadcrumb-separator" />
          <Link to="/naatkhawans" className="breadcrumb-link">Naat Khawans</Link>
          <ChevronRight size={16} className="breadcrumb-separator" />
          <span className="breadcrumb-current">{naatKhawan?.name || 'Loading...'}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content-section">
        <div className="content-container">
          {/* Audio Files */}
          <div className="audio-section">
            <div className="section-header">
              <div className="section-header-left">
                <Link to="/naatkhawans" className="section-title-link">
                  <h3>Naats by {naatKhawan.name}</h3>
                </Link>
                <div className="header-actions">
                  <span className="audio-count">
                    {pagination.totalPages > 1
                      ? `${pagination.total} total tracks (Page ${pagination.page} of ${pagination.totalPages})`
                      : `${audioFiles.length} ${initial ? `tracks starting with '${initial}'` : 'tracks'}`
                    }
                  </span>
                  {audioFiles.length > 0 && (
                    <button
                      className="nk-play-all-btn"
                      onClick={() => {
                        const fullAudioFiles = audioFiles.map(convertToAudioFile);
                        playAudio(fullAudioFiles[0], fullAudioFiles);
                        const isPlayerMinimized = !document.querySelector('.global-audio-player:not(.minimized)');
                        showToast(`Playing all ${audioFiles.length} naats`, 'success', 3000, isPlayerMinimized ? 'bottom-right' : 'top-right');
                      }}
                    >
                      <Play size={16} />
                      Play All
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="alphabet-filter" role="toolbar" aria-label="Filter by initial letter">
              <button
                type="button"
                className={`alphabet-btn reset ${initial === '' ? 'active' : ''}`}
                onClick={() => setInitial('')}
                aria-pressed={initial === ''}
              >
                All
              </button>
              {alphabet.map(letter => {
                const isAvailable = availableLetters.has(letter);
                const isActive = initial === letter;
                return (
                  <button
                    key={letter}
                    type="button"
                    className={`alphabet-btn ${isActive ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                    onClick={() => handleInitialClick(letter)}
                    aria-pressed={isActive}
                    aria-disabled={!isAvailable}
                    disabled={!isAvailable}
                    title={!isAvailable ? `No naats starting with ${letter}` : `Filter by ${letter}`}
                  >
                    {letter}
                  </button>
                );
              })}
              
            </div>
            
            {audioFiles.length > 0 ? (
              <>
                <div className="naat-card-grid">
                  {audioFiles.map((audio, index) => {
                    const audioFile = convertToAudioFile(audio);
                    const globalIndex = (pagination.page - 1) * 15 + index + 1;
                    return (
                      <NaatCard
                        key={audio.id}
                        naat={audioFile}
                        serialNumber={globalIndex}
                        isPlaying={currentAudio?.id === audio.id && isPlaying}
                        isFavorite={isFavorite(audio.id)}
                        onPlay={handlePlayAudio}
                        onPause={() => togglePlay()}
                        onAddToQueue={handleAddToQueue}
                        onToggleFavorite={handleToggleFavorite}
                        onDownloadComplete={handleDownloadComplete}
                      />
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {pagination.totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPrev}
                    >
                      Previous
                    </button>

                    <div className="pagination-numbers">
                      {/* Show page numbers with smart truncation */}
                      {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else {
                          // Smart pagination: show current page and surrounding pages
                          const start = Math.max(1, pagination.page - 2);
                          const end = Math.min(pagination.totalPages, start + 4);
                          pageNum = start + i;
                          if (pageNum > end) return null;
                        }

                        return (
                          <button
                            key={pageNum}
                            className={`pagination-number ${pagination.page === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      }).filter(Boolean)}
                    </div>

                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNext}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-audio">
                <Music size={48} />
                <p>No audio files available for this NaatKhawan</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NaatKhawanDetailPage;